<script lang="ts">
  import {
    Captions,
    CaptionsOff,
    Cog,
    FastForward,
    Pause,
    Play,
    Rewind,
    SkipBack,
    SkipForward,
    X,
  } from '@lucide/svelte'
  import Button from '../../../components/button/Button.svelte'
  import Color from '../../../components/color-picker/Color.svelte'
  import Slider from '../../../components/slider/Slider.svelte'
  import { ceil } from '../../../helpers/math'
  import Input from '../../../components/input/Input.svelte'
  import Switch from '../../../components/switch/Switch.svelte'
  import i18n from '../../../i18n'
  import Tooltip from '../../../components/tooltip/Tooltip.svelte'

  type Props = {
    printer?: { name: string, color: string }
    previewProgress: number
    displayCaptions: boolean
    displayG0: boolean
    gcodeSize: number
  }

  let {
    printer,
    previewProgress = $bindable(),
    displayCaptions = $bindable(),
    displayG0 = $bindable(),
    gcodeSize,
  }: Props = $props()
  let displaySettings = $state(false)
  let interval = $state<NodeJS.Timeout>()

  const stopPlaying = () => {
    if (interval) {
      clearInterval(interval)
      interval = undefined
    }
  }

  const startPlaying = () => {
    if (previewProgress >= gcodeSize) {
      previewProgress = 0
    }
    interval = setInterval(() => {
      if (previewProgress >= gcodeSize && interval) {
        stopPlaying()
      }
      previewProgress = Math.min(
        gcodeSize,
        previewProgress + ceil(gcodeSize / 180),
      )
    }, 300)
  }
</script>

<div class="player-container">
  <div class="slider-content-container">
    <div class="slider-top-bar-container">
      <div class="printer-name">
        {#if printer}
          <Color color={printer.color} />
          {printer.name}
        {/if}
      </div>
      <div class="buttons-container">
        <Button
          onclick={() => (previewProgress = 0)}
          variant="ghost"
        >
          <SkipBack />
        </Button>
        <Button
          onclick={() => (previewProgress -= 1000)}
          variant="ghost"
        >
          <Rewind />
        </Button>
        {#if interval}
          <Button onclick={stopPlaying}>
            <Pause />
          </Button>
        {:else}
          <Button onclick={startPlaying}>
            <Play />
          </Button>
        {/if}
        <Button
          onclick={() => (previewProgress += 1000)}
          variant="ghost"
        >
          <FastForward />
        </Button>
        <Button
          onclick={() => (previewProgress = gcodeSize)}
          variant="ghost"
        >
          <SkipForward />
        </Button>
      </div>
      <div class="additional-actions-container">
        <Tooltip
          placement="top"
          title={$i18n.t('projectPreview.displayGcode')}
        >
          <Button
            onclick={() => (displayCaptions = !displayCaptions)}
            variant="ghost"
          >
            {#if displayCaptions}
              <CaptionsOff />
            {:else}
              <Captions />
            {/if}
          </Button>
        </Tooltip>
        <Tooltip
          placement="top"
          title={$i18n.t('projectPreview.settings.title')}
        >
          <Button
            onclick={() => displaySettings = !displaySettings}
            variant="ghost"
          >
            <Cog />
          </Button>
        </Tooltip>
        {#if displaySettings}
          <div class="settings">
            <div class="card-header">
              {$i18n.t('projectPreview.settings.title')}
              <Button
                onclick={() => (displaySettings = false)}
                variant="ghost"
              >
                <X />
              </Button>
            </div>
            <div class="card-body">
              <div>
                {$i18n.t('projectPreview.settings.displayG0')}
                <Switch
                  label={$i18n.t('projectPreview.settings.displayG0')}
                  bind:checked={displayG0}
                />
              </div>
            </div>
          </div>
        {/if}
      </div>
    </div>
    <div class="slider-container">
      <div class="progress-counter">
        <Tooltip title="⇧ + ▼: -100 | ▼: -10 | ◄: -1 | ►: +1 | ▲: +10 | ⇧ + ▲: +100">
          <Input
            max={gcodeSize}
            min={1}
            sizeVariant="sm"
            type="number"
            bind:value={previewProgress}
          />
        </Tooltip>
      </div>

      <Slider
        defaultValue={1}
        max={gcodeSize}
        min={1}
        step={1}
        tickStep={gcodeSize}
        bind:value={previewProgress}
      />
      <div class="progress-counter">
        {gcodeSize}
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .player-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background: var(--background);

    .slider-content-container {
      padding: 1rem;
      flex: 1;
      border-radius: 0.5rem;
      max-width: 64rem;
      display: flex;
      align-items: stretch;
      flex-direction: column;
      gap: 1rem;

      .slider-top-bar-container {
        display: flex;
        flex-direction: row;
        align-items: center;

        .printer-name {
          flex: 1;
          display: flex;
          flex-direction: row;
          gap: 0.5rem;
          align-items: center;
          justify-content: flex-start;
        }

        .additional-actions-container {
          flex: 1;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-end;
          position: relative;

          .settings {
            align-items: stretch;
            background: var(--background);
            border-radius: 0.5rem;
            border: 1px solid var(--grid-stroke);
            bottom: 4.5rem;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            min-width: 16rem;
            position: absolute;

            .card-header {
              align-items: center;
              border-bottom: 1px solid var(--grid-stroke);
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              padding: 0.5rem 0.5rem 0.5rem 1rem;
            }

            .card-body {
              align-items: stretch;
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
              justify-content: flex-start;
              padding: 1rem 1rem;

              > div {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
              }
            }

            &::before {
              content: "";
              width: 0;
              height: 0;
              border-left: 0.5rem solid transparent;
              border-right: 0.5rem solid transparent;
              border-top: 0.5rem solid var(--background);
              position: absolute;
              bottom: -0.5rem;
              right: 0.75rem;
            }
          }
        }

        .buttons-container {
          display: flex;
          flex-direction: row;
          gap: 0.75rem;
          align-items: center;
          justify-content: center;
        }
      }

      .slider-container {
        display: flex;
        flex-direction: row;
        gap: 1rem;

        .progress-counter {
          width: 6rem;

          &:last-of-type {
            text-align: right;
          }
        }
      }
    }
  }
</style>
